import * as React from 'react'
import { SetFormSubmission, submissionService } from '@oneblink/apps-react'
import { FormTypes, SubmissionTypes } from '@oneblink/types'

import useSaveDraft from '../hooks/useSaveDraft'
import useCancelForm from '../hooks/useCancelForm'
import useAutoSaveApprovalsControlledForm from '../hooks/useAutoSaveApprovalsControlledForm'
import AutoSaveApprovalControlledForm from './AutoSaveApprovalsControlledForm'
import { ExecutedLookups } from '@oneblink/apps-react'
import isDeepEqual from '../lib/is-deep-equal'

type Props = {
  form: FormTypes.Form
  initialSubmission?: Record<string, unknown>
  onSubmit: (newFormSubmission: submissionService.NewFormSubmission) => void
  buttons: Record<string, unknown> | undefined
  updateSubmission: (
    submission: Record<string, unknown>,
  ) => Record<string, unknown>
  updateDefinition: (
    submission: Record<string, unknown>,
    definition: FormTypes.Form,
  ) => FormTypes.Form
  isSubmitting: boolean
  serverValidationMessage: string | undefined
  clearServerValidationMessage: () => void
}

export default function AutoSaveControlledForm({
  form,
  initialSubmission,
  onSubmit,
  updateSubmission,
  updateDefinition,
  isSubmitting,
  buttons,
  serverValidationMessage,
  clearServerValidationMessage,
}: Props) {
  const [onCancel, cancelError] = useCancelForm(form)
  const [onSaveDraft, isSavingDraft] = useSaveDraft({
    formDefinition: form,
    preFillData: initialSubmission,
  })

  const {
    definition,
    liveSubmission,
    setFormSubmission,
    handleSubmit,
    handleCancel,
    handleSaveDraft,
    isAutoSaveSubmissionAvailable,
    isLoadingAutoSaveSubmission,
    startNewSubmission,
    continueAutoSaveSubmission,
    executedLookups,
  } = useAutoSaveApprovalsControlledForm({
    form,
    initialSubmission,
    onSubmit,
    onCancel,
    onSaveDraft,
  })

  /* update definition here */
  const customDefinition = React.useMemo(() => {
    const updatedDefinition = updateDefinition(liveSubmission, definition)
    // pages are memo'd so we need new objects for the updated definition to render
    return {
      ...updatedDefinition,
      elements: updatedDefinition.elements.map((el) => ({ ...el })),
    }
  }, [definition, liveSubmission, updateDefinition])

  /* update submission data here */
  const customSetFormSubmission: SetFormSubmission = React.useCallback(
    (formSubmission) => {
      setFormSubmission((currentState) => {
        const newFormSubmission: {
          definition: FormTypes.Form
          submission: SubmissionTypes.S3SubmissionData['submission']
          lastElementUpdated: FormTypes.FormElement | undefined
          executedLookups: ExecutedLookups
        } =
          typeof formSubmission === 'function'
            ? formSubmission(currentState)
            : formSubmission

        const updatedSubmission = updateSubmission(newFormSubmission.submission)

        /*
          Calculation elements re-run and write their result back whenever the
          `onChange` handed to them changes identity, even when the value is
          identical. Returning a new state object for one of those writes gives
          the renderer a new definition and a new `onChange` for every element,
          which runs the calculations again, and the form never settles. Keeping
          the current state when nothing actually changed breaks that cycle.

          `lastElementUpdated` is deliberately not compared: the competing
          calculations report different elements on every pass, so including it
          would mean never finding the states equal. A write that leaves the
          submission untouched has nothing for a lookup to react to anyway.
        */
        if (
          newFormSubmission.definition === currentState.definition &&
          isDeepEqual(updatedSubmission, currentState.submission) &&
          isDeepEqual(
            newFormSubmission.executedLookups,
            currentState.executedLookups,
          )
        ) {
          return currentState
        }

        return {
          ...currentState,
          ...newFormSubmission,
          submission: updatedSubmission,
        }
      })
    },
    [setFormSubmission, updateSubmission],
  )

  return (
    <>
      <AutoSaveApprovalControlledForm
        definition={customDefinition}
        submission={liveSubmission}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        onSaveDraft={handleSaveDraft}
        setFormSubmission={customSetFormSubmission}
        cancelError={cancelError}
        isSubmitting={isSubmitting}
        isSavingDraft={isSavingDraft}
        isLoadingAutoSaveSubmission={isLoadingAutoSaveSubmission}
        isAutoSaveSubmissionAvailable={isAutoSaveSubmissionAvailable}
        startNewSubmission={startNewSubmission}
        continueAutoSaveSubmission={continueAutoSaveSubmission}
        buttons={buttons}
        executedLookups={executedLookups}
        serverValidationMessage={serverValidationMessage}
        clearServerValidationMessage={clearServerValidationMessage}
      />
    </>
  )
}
